import styled, { css } from "styled-components";
import { style } from "../style";
import { hexWithOpacityToRBGA } from "../utils/hexWithOpacityToRBGA";

type GridTemplate = {
  gridTemplateRows?: string;
  gridTemplateColumns?: string;
  gridTemplateAreas?: string;
  columnGap?: string;
  rowGap?: string;
};

type TextStyleProps = {
  $disabled: boolean;
  $highlight: boolean;
};

type BaseContainerProps = {
  $horizontal: boolean;
};

type ContainerProps = {
  $highlight: boolean;
  $disabled: boolean;
  $invalid: boolean;
  $focused: boolean;
  $compact: boolean;
  $hideOverflow: boolean;
  $horizontal: boolean;
  $flatTopLeft: boolean;
  $flatTopRight: boolean;
  $flatBottomLeft: boolean;
  $flatBottomRight: boolean;
};

type GridProps = {
  $gridTemplate: GridTemplate;
  $compact: boolean;
};

const BaseContainer = styled.div<BaseContainerProps>`
  all: unset;
  display: block;

  ${(props) => {
    /**
     * When the button is horizontally arranged, want to ensure a max width
     * while making it `100%` in a vertical arrangement. The horizontal should
     * only take up as much room as is necessary, while the vertical should take
     * up the entire parent.
     */
    if (props.$horizontal) {
      return {
        maxWidth: "1000px",
      };
    } else {
      return {
        width: "100%",
      };
    }
  }}
`;

const Container = styled.div<ContainerProps>`
  all: unset;
  display: flex;

  ${(props) => {
    if (props.$hideOverflow) {
      return {
        height: props.$compact ? "48px" : "65px",
      };
    } else {
      return {
        minHeight: props.$compact ? "48px" : "65px",
      };
    }
  }}

  min-width: ${(props) => (props.$compact ? "initial" : "300px")};

  ${(props) => {
    /**
     * When the button is horizontally arranged, want to ensure a max width
     * while making it `100%` in a vertical arrangement. The horizontal should
     * only take up as much room as is necessary, while the vertical should take
     * up the entire parent.
     */
    if (props.$horizontal) {
      return {
        maxWidth: "1000px",
      };
    } else {
      return {
        width: "100%",
      };
    }
  }}

  ${(props) => {
    return {
      borderTopLeftRadius: props.$flatTopLeft ? 0 : style.vars.borderRadius.base,
      borderTopRightRadius: props.$flatTopRight ? 0 : style.vars.borderRadius.base,
      borderBottomLeftRadius: props.$flatBottomLeft ? 0 : style.vars.borderRadius.base,
      borderBottomRightRadius: props.$flatBottomRight ? 0 : style.vars.borderRadius.base,
    };
  }}

  box-sizing: border-box;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};

  color: ${style.colors.grey.darkest};
  outline-offset: ${style.vars.borderWidth.thin};

  background-color: ${style.colors.white};

  :hover {
    background-color: ${style.colors.grey.lightest};
  }

  :active:not(:focus-within) {
    ${(props) => {
      if (props.$disabled) {
        return;
      }

      return css`
        color: ${style.colors.blue.dark};
        border-color: ${style.colors.blue.dark};
        background-color: ${style.colors.blue.lightest};
        pointer: cursor;

        ${SubtitleText} {
          color: ${style.colors.blue.dark};
        }
      `;
    }}
  }

  && {
    ${(props) => {
      // same as the active
      if (props.$highlight) {
        return {
          borderColor: style.colors.blue.dark,
          backgroundColor: style.colors.blue.lightest,
          color: style.colors.blue.dark,
        };
      }
    }}

    ${(props) => {
      if (props.$focused) {
        return {
          outline: `${style.vars.borderWidth.base} dotted ${style.colors.grey.dark}`,
        };
      }
    }}

    ${(props) => {
      if (props.$disabled) {
        return {
          borderColor: hexWithOpacityToRBGA(style.colors.grey.primary, 0.3),
          backgroundColor: hexWithOpacityToRBGA(style.colors.white, 0.3),
          cursor: "unset",
          color: style.colors.grey.darkest,
          outline: "none",
        };
      }

      if (props.$invalid) {
        return {
          borderColor: style.colors.red.dark,
        };
      }
    }}
  }
`;

Container.displayName = "FatButton.Container";

const Grid = styled.label<GridProps>`
  all: unset;
  display: grid;
  flex-grow: 1;

  padding: ${(props) => (props.$compact ? "0 12px" : "8px 12px")};
  ${(props) => props.$gridTemplate}
`;

Grid.displayName = "FatButton.Grid";

const TitleText = styled.div`
  all: unset;

  font-size: ${style.vars.fontSize.base};
  font-weight: ${style.vars.fontWeight.bold};
  line-height: 1.5;
`;

TitleText.displayName = "FatButton.TitleText";

const TitleWrapper = styled.div<{ $faded: boolean; $hideOverflow: boolean }>`
  all: unset;

  grid-area: title;
  align-self: center;
  font-size: ${style.vars.fontSize.base};
  line-height: 1.5;
  opacity: ${(props) => (props.$faded ? 0.3 : 1)};

  ${(props) => {
    if (props.$hideOverflow) {
      return {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }
  }}
`;

TitleWrapper.displayName = "FatButton.TitleWrapper";

const SubtitleText = styled.div<TextStyleProps>`
  all: unset;

  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.base};
  line-height: 1.5;
  color: ${(props) => (props.$highlight && !props.$disabled ? style.colors.blue.dark : style.colors.grey.dark)};
`;

SubtitleText.displayName = "FatButton.SubtitleText";

const SubtitleWrapper = styled.div<{ $faded: boolean; $hideOverflow: boolean }>`
  all: unset;

  grid-area: subtitle;
  align-self: baseline;
  font-size: ${style.vars.fontSize.small};
  line-height: 1.5;
  opacity: ${(props) => (props.$faded ? 0.3 : 1)};

  ${(props) => {
    if (props.$hideOverflow) {
      return {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxHeight: `calc(${style.vars.fontSize.small} * 1.5)`,
      };
    }
  }}
`;

SubtitleWrapper.displayName = "FatButton.SubtitleWrapper";

const IconWrapper = styled.div<{ $faded: boolean }>`
  all: unset;

  grid-area: icon;
  align-self: center;

  ${(props) => {
    if (props.$faded) {
      return {
        opacity: 0.3,
        color: style.colors.grey.darkest,
      };
    }

    return {
      opacity: 1,
    };
  }}
`;

IconWrapper.displayName = "FatButton.IconWrapper";

const RightSideWrapper = styled.div<{ $faded: boolean }>`
  all: unset;
  width: auto;
  grid-area: rhs;
  align-self: center;
  justify-self: end;
  font-size: ${style.vars.fontSize.base};
  line-height: 1;
  opacity: ${(props) => (props.$faded ? 0.3 : 1)};

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

RightSideWrapper.displayName = "FatButton.RightSideWrapper";

type ExtensionProps = {
  $faded: boolean;
};

const Extension = styled.div<ExtensionProps>`
  all: unset;
  display: inline-block;
  width: 100%;

  padding: ${style.vars.space[3]};

  box-sizing: border-box;
  background-color: ${style.colors.white};

  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: ${style.vars.borderRadius.base};
  border-bottom-right-radius: ${style.vars.borderRadius.base};

  ${(props) => {
    if (props.$faded) {
      return { borderColor: hexWithOpacityToRBGA(style.colors.grey.primary, 0.3) };
    }
  }}
`;

/**
 * FatButton works by allowing you to specify a grid template for the Container, while
 * the rest of the elements are placed inside of the Container and already have `grid-area`
 * CSS props assigned to them. The grid-template-areas as part of the `gridTemplate` prop
 * should use the `title`, `subtitle`, `icon`, `rhs-lower` and `rhs-upper` grid areas in order to place these
 * components. Additional grid areas can also be included if needed, we just don't create a component
 * for it here.
 */
export const FatButton = {
  BaseContainer,
  Container,
  Grid,
  TitleText,
  TitleWrapper,
  SubtitleText,
  SubtitleWrapper,
  IconWrapper,
  RightSideWrapper,
  Extension,
};
